import React, {FC, useContext, useEffect, useMemo, useState} from 'react';
import {Icons} from '../../../assets';
import {PerforationDivider} from '../../../components/perforation-divider';
import {ContactItem, ProfileHeaderCard} from '../../../components/profile-header-card';
import {AccountContext, AccountContextInterface} from '../../../contexts';
import {Account} from '../../../models';
import ElevatorPitch from './elevator-pitch.component';
import ExperienceEducation from './experience-education.component';
import SkillGraph from './skill-graph.component';
import {PublicCredSection} from './public-profile/public-cred-section';
import {PublicReccoSection} from './public-profile/public-recco-section';

type AuraSectionProps = {
  accountId: string;
};

const AuraSection: FC<AuraSectionProps> = ({accountId}) => {
  const {accountService} = useContext<AccountContextInterface>(AccountContext);
  const [account, setAccount] = useState<Account>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const {data: accountDetails} = await accountService.getAccount(accountId);
        setAccount(accountDetails);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accountId, accountService]);

  const contactItems = useMemo<ContactItem[]>(() => {
    if (!account) {
      return [];
    }

    const items: ContactItem[] = [];

    if (account.email) {
      items.push({
        href: `mailto:${account.email}`,
        icon: Icons.EMAIL,
        label: account.email,
      });
    }

    if (account.linkedInProfileUrl) {
      items.push({
        href: account.linkedInProfileUrl,
        icon: Icons.LINKDIN_ICON,
        label: '',
        newTab: true,
      });
    }

    if (account.instagramProfileUrl) {
      items.push({
        href: account.instagramProfileUrl,
        icon: Icons.INSTAGRAM_ICON,
        label: '',
        newTab: true,
      });
    }

    if (account.facebookProfileUrl) {
      items.push({
        href: account.facebookProfileUrl,
        icon: Icons.LINK_ICON,
        label: '',
        newTab: true,
      });
    }

    return items;
  }, [account]);

  const displayName = account ? Account.getDisplayName(account) : 'Aura Member';

  if (loading || !account) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="mx-auto max-w-[80vw]">
        <section className="mb-8 rounded-2xl bg-gray-50/50 py-4">
          <ProfileHeaderCard
            contactItems={contactItems}
            imageUrl={account.profilePictureResource?.url}
            name={displayName}
            nameHeadingClassName="text-3xl font-semibold text-gray-900"
            famCount={account.famsCount}
            followersCount={account.followersCount}
            subtitle={account.bio || account.title || undefined}
            isFollowing={account.isFollowing}
            onToggleFollow={() => {}}
          />
        </section>

        <section className="grid gap-8 rounded-2xl bg-gray-100 p-6 sm:px-8 md:px-10 lg:grid-cols-[1fr_auto_1.5fr_auto_1fr]">
          <div className="flex flex-col gap-6 max-h-[calc(100vh-220px)] overflow-y-auto">
            <PublicCredSection accountId={account.id} />
          </div>
          <div className="hidden lg:block">
            <PerforationDivider className="h-full" />
          </div>
          <div className="flex flex-col gap-6 max-h-[calc(100vh-220px)] overflow-y-auto">
            <ElevatorPitch account={account} />
            {/* <SkillGraph accountId={account.id} /> */}
            <ExperienceEducation account={account} />
          </div>
          <div className="hidden lg:block">
            <PerforationDivider className="h-full" dense />
          </div>
          <div className="flex flex-col gap-6 max-h-[calc(100vh-220px)] overflow-y-auto">
            <PublicReccoSection accountId={account.id} />
          </div>
        </section>
      </div>
    </div>
  );
};

export default AuraSection;
